import type { FlatEvent, SessionSummary, VisitDetail, VisitSurface } from "@backstory/protocol";

/**
 * Visit playback is organized around surfaces: the browsing contexts (tabs or
 * separate windows) that recorded a visit. A browser does not reliably say
 * which of the two a same-origin context is, so the product calls both a
 * surface and treats them the same way.
 *
 * Everything here works in absolute epoch milliseconds — the visit clock —
 * because switching surfaces has to preserve the moment, not the offset into
 * whichever segment happened to be playing.
 */

/** What a surface was doing at a moment on the visit clock. */
export type SurfaceStatus =
  /** On screen and holding window focus: where the user was working. */
  | "focused"
  /** On screen but not focused, e.g. a second window beside the focused one. */
  | "visible"
  /** Recording, but the surface was backgrounded. */
  | "hidden"
  /** The surface exists in this visit but has no segment covering this moment. */
  | "gap"
  /** The moment is before this surface opened. */
  | "before"
  /** The moment is after this surface's last recorded segment. */
  | "closed";

export interface SurfaceMoment {
  status: SurfaceStatus;
  /** Whether a segment of this surface actually recorded this moment. */
  covered: boolean;
  /** Segment covering the moment, or the nearest one to offer instead. */
  segment?: SessionSummary;
  /** Offset into that segment, for seeking. */
  offsetMs: number;
}

const startOf = (s: SessionSummary) => new Date(s.startedAt).getTime();
const endOf = (s: SessionSummary) => new Date(s.lastEventAt).getTime();

export function surfaceStart(surface: VisitSurface): number {
  return new Date(surface.startedAt).getTime();
}

export function surfaceEnd(surface: VisitSurface): number {
  return new Date(surface.lastEventAt).getTime();
}

/** Visit bounds from its surfaces; falls back to the flat segment list. */
export function visitBounds(visit: VisitDetail): { start: number; end: number } {
  const segs = visit.segments ?? [];
  const start = Math.min(new Date(visit.startedAt).getTime(), ...segs.map(startOf));
  const end = Math.max(new Date(visit.lastSeenAt).getTime(), ...segs.map(endOf));
  return { start, end: Math.max(end, start + 1) };
}

/**
 * Surfaces of a visit, tolerating responses from an API that predates surface
 * grouping by treating the whole visit as one surface.
 */
export function visitSurfaces(visit: VisitDetail): VisitSurface[] {
  if (visit.surfaces?.length) return visit.surfaces;
  const segs = [...(visit.segments ?? [])].sort((a, b) => startOf(a) - startOf(b));
  if (!segs.length) return [];
  const first = segs[0]!;
  const last = segs[segs.length - 1]!;
  return [
    {
      surfaceId: first.surfaceId ?? 0,
      url: first.url,
      title: first.title,
      startedAt: first.startedAt,
      lastEventAt: last.lastEventAt,
      durationMs: endOf(last) - startOf(first),
      eventCount: segs.reduce((a, s) => a + s.eventCount, 0),
      errorCount: segs.reduce((a, s) => a + s.errorCount, 0),
      segments: segs,
    },
  ];
}

export function surfaceOf(surfaces: VisitSurface[], sessionId: string): VisitSurface | undefined {
  return surfaces.find((sf) => sf.segments.some((s) => s.id === sessionId));
}

/** A short, human label: the page title, else the path, else the host. */
export function surfaceLabel(surface: VisitSurface): string {
  if (surface.title?.trim()) return surface.title.trim();
  try {
    const u = new URL(surface.url);
    return u.pathname === "/" ? u.host : u.pathname;
  } catch {
    return surface.url || `Surface ${surface.surfaceId + 1}`;
  }
}

export function surfaceHost(surface: VisitSurface): string {
  try {
    return new URL(surface.url).host;
  } catch {
    return "";
  }
}

/**
 * Where a surface stands at `absTs`, and which of its segments to play. When
 * the moment falls in a recording gap, the nearest segment is returned so the
 * UI can offer to jump there instead of showing an empty pane.
 */
export function momentOn(surface: VisitSurface, absTs: number): SurfaceMoment {
  const segs = [...surface.segments].sort((a, b) => startOf(a) - startOf(b));
  if (!segs.length) return { status: "gap", covered: false, offsetMs: 0 };
  for (const s of segs) {
    // Covered: the state events decide whether that meant visible or hidden.
    if (absTs >= startOf(s) && absTs <= endOf(s)) return { status: "hidden", covered: true, segment: s, offsetMs: absTs - startOf(s) };
  }
  if (absTs < startOf(segs[0]!)) return { status: "before", covered: false, segment: segs[0], offsetMs: 0 };
  const last = segs[segs.length - 1]!;
  if (absTs > endOf(last)) return { status: "closed", covered: false, segment: last, offsetMs: last.durationMs };
  // Between two segments of the same surface: offer the next one.
  const next = segs.find((s) => startOf(s) > absTs) ?? last;
  return { status: "gap", covered: false, segment: next, offsetMs: 0 };
}

/**
 * State changes a surface reported, newest last. Built from flattened
 * `visibility` events, whose subtype is the page state and whose name carries
 * window focus (absent on recordings made before focus was captured).
 */
export interface SurfaceStateChange {
  ts: number;
  visible: boolean;
  focused: boolean;
}

export function stateChanges(events: FlatEvent[]): SurfaceStateChange[] {
  return events
    .filter((e) => e.type === "visibility")
    .map((e) => ({ ts: e.ts, visible: e.subtype !== "hidden", focused: e.name === "focused" }))
    .sort((a, b) => a.ts - b.ts);
}

/**
 * Resolve a surface's status at a moment, refining the segment-coverage answer
 * with the last state the surface reported at or before that moment. Without
 * state events a covered surface counts as visible, which is how a single-tab
 * recording has always behaved.
 */
export function statusAt(surface: VisitSurface, absTs: number, changes?: SurfaceStateChange[]): SurfaceMoment {
  const m = momentOn(surface, absTs);
  if (!m.covered) return m;
  const last = [...(changes ?? [])].filter((c) => c.ts <= absTs).pop();
  if (!last) return { ...m, status: "visible" };
  return { ...m, status: !last.visible ? "hidden" : last.focused ? "focused" : "visible" };
}

/** When a surface last reported holding focus at or before `absTs`. */
function lastFocusAt(changes: SurfaceStateChange[] | undefined, absTs: number): number {
  const focused = (changes ?? []).filter((c) => c.ts <= absTs && c.visible && c.focused);
  return focused.length ? focused[focused.length - 1]!.ts : -Infinity;
}

/**
 * Status of every surface at one moment, reconciled across the visit. Each
 * surface reports its own state, so two of them can both claim focus — one
 * simply never recorded losing it. Only one browsing context holds focus at a
 * time, so the most recent claim keeps it and the others read as on screen.
 */
export function statusesAt(surfaces: VisitSurface[], absTs: number, states: Record<number, SurfaceStateChange[]>): Record<number, SurfaceStatus> {
  const out: Record<number, SurfaceStatus> = {};
  let holder = { id: -1, ts: -Infinity };
  for (const sf of surfaces) {
    out[sf.surfaceId] = statusAt(sf, absTs, states[sf.surfaceId]).status;
    if (out[sf.surfaceId] !== "focused") continue;
    const ts = lastFocusAt(states[sf.surfaceId], absTs);
    if (ts >= holder.ts) holder = { id: sf.surfaceId, ts };
  }
  for (const sf of surfaces) {
    if (out[sf.surfaceId] === "focused" && sf.surfaceId !== holder.id) out[sf.surfaceId] = "visible";
  }
  return out;
}

export const STATUS_LABELS: Record<SurfaceStatus, string> = {
  focused: "Focused",
  visible: "On screen",
  hidden: "Background",
  gap: "Not recorded here",
  before: "Not open yet",
  closed: "Closed",
};

/** Statuses that mean the surface was on screen at that moment. */
export function isOnScreen(status: SurfaceStatus): boolean {
  return status === "focused" || status === "visible";
}

export type StageMode = "auto" | "single";

export interface PaneSelection {
  primary: VisitSurface;
  secondary?: VisitSurface;
}

/**
 * Which surfaces the stage shows. Auto-follow puts the focused surface first
 * and pairs it with another surface that was on screen at the same moment, so
 * a hand-off between two windows is visible without any clicking. The stage
 * caps at two panes; anything else stays in the rail, because four thumbnails
 * are worse than two readable replays. A pinned surface always keeps its slot.
 */
export function pickPanes(
  surfaces: VisitSurface[],
  statusOf: (surface: VisitSurface) => SurfaceStatus,
  opts: { mode: StageMode; routed: VisitSurface; pinned?: number | null },
): PaneSelection {
  const { mode, routed, pinned } = opts;
  const pinnedSurface = pinned != null ? surfaces.find((s) => s.surfaceId === pinned) : undefined;
  if (mode === "single") return { primary: pinnedSurface ?? routed };

  const onScreen = surfaces.filter((s) => isOnScreen(statusOf(s)));
  const focused = onScreen.find((s) => statusOf(s) === "focused");
  // The routed surface stays primary while it is on screen: the user chose it,
  // and DevTools, citations, and the event list are all bound to it.
  const primary = isOnScreen(statusOf(routed)) ? routed : (focused ?? pinnedSurface ?? routed);
  const others = onScreen.filter((s) => s.surfaceId !== primary.surfaceId);
  const secondary = pinnedSurface && pinnedSurface.surfaceId !== primary.surfaceId ? pinnedSurface : others[0];
  return { primary, secondary };
}

/**
 * Which segment should play a moment on the visit clock.
 *
 * The surface being pointed at wins when it recorded that moment. Otherwise the
 * moment belongs to whichever surface was recording then — this is how a
 * citation or a trigger from another tab lands on the right replay instead of
 * on the nearest segment of the tab that happens to be on the stage. With no
 * surface recording it at all, the nearest segment of the target surface is
 * offered so the user still moves somewhere sensible.
 */
export function resolveMoment(surfaces: VisitSurface[], target: VisitSurface | undefined, absTs: number, opts: { anySurface?: boolean } = {}): SurfaceMoment | null {
  const here = target ? momentOn(target, absTs) : null;
  if (here?.covered) return here;
  if (opts.anySurface) {
    const elsewhere = surfaces.map((sf) => momentOn(sf, absTs)).find((m) => m.covered);
    if (elsewhere) return elsewhere;
  }
  return here;
}

/** Next trigger (error or alarm) at or after `absTs`, across every surface. */
export function nextTrigger(segments: SessionSummary[], triggers: Record<string, number[]>, absTs: number): { sessionId: string; ts: number } | null {
  const all: Array<{ sessionId: string; ts: number }> = [];
  for (const s of segments) for (const ts of triggers[s.id] ?? []) all.push({ sessionId: s.id, ts });
  all.sort((a, b) => a.ts - b.ts);
  return all.find((t) => t.ts > absTs + 1) ?? null;
}
